import { FormEvent, useEffect, useMemo, useState } from "react";

import instance from "../../api/instance";
import endpoints from "../../api/endpoints";
import { showErrorToast, showSuccessToast } from "../../utils/toast";

type Privilege = {
  id: string;
  value: string;
  label: string;
  apiKey: string;
};

const PRIVILEGES: Privilege[] = [
  { id: "p-1", value: "1", label: "DashBoard", apiKey: "dashboard" },
  { id: "p-2", value: "2", label: "Market Analysis", apiKey: "market_analysis" },
  { id: "p-4", value: "4", label: "User List", apiKey: "user_list" },
  { id: "p-5", value: "5", label: "Insert User", apiKey: "insert_user" },
  { id: "p-8", value: "8", label: "Account Statement", apiKey: "account_statement" },
  { id: "p-9", value: "9", label: "Party Win Loss", apiKey: "party_win_loss" },
  { id: "p-10", value: "10", label: "Current Bets", apiKey: "current_bets" },
  { id: "p-12", value: "12", label: "General Lock", apiKey: "general_lock" },
  { id: "p-13", value: "13", label: "Casino Result", apiKey: "casino_result" },
  { id: "p-14", value: "14", label: "Live Casino Result", apiKey: "live_casino_result" },
  { id: "p-15", value: "15", label: "Our Casino", apiKey: "our_casino" },
  { id: "p-16", value: "16", label: "Events", apiKey: "events" },
  { id: "p-17", value: "17", label: "Market Search Analysis", apiKey: "market_search_analysis" },
  { id: "p-19", value: "19", label: "Login User creation", apiKey: "login_user_creation" },
  { id: "p-54", value: "54", label: "Withdraw", apiKey: "withdraw" },
  { id: "p-55", value: "55", label: "Deposit", apiKey: "deposit" },
  { id: "p-56", value: "56", label: "Credit Reference", apiKey: "credit_reference" },
  { id: "p-57", value: "57", label: "User Info", apiKey: "user_info" },
  { id: "p-58", value: "58", label: "User Password Change", apiKey: "user_password_change" },
  { id: "p-59", value: "59", label: "User Lock", apiKey: "user_lock" },
  { id: "p-60", value: "60", label: "Bet Lock", apiKey: "bet_lock" },
  { id: "p-91", value: "91", label: "active user", apiKey: "active_user" },
  { id: "p-104", value: "104", label: "Agent Assign", apiKey: "agent_assign" },
  { id: "p-111", value: "111", label: "User Register Report", apiKey: "user_register_report" },
  { id: "p-112", value: "112", label: "Total Profitloss", apiKey: "total_profitloss" },
  { id: "p-113", value: "113", label: "User Winloss", apiKey: "user_winloss" },
];

type SubAdminRow = {
  id: string;
  username: string;
  fullName: string;
  isActive: boolean;
  permissions: Record<string, boolean>;
};
type CreateAccountField = "uname" | "fullname" | "password" | "cpass";

const hasPermissionFlag = (privilege: Privilege, permissions?: Record<string, boolean>) => {
  const candidates = [
    privilege.apiKey,
    privilege.apiKey.toLowerCase(),
    privilege.apiKey.toUpperCase(),
    privilege.label,
    privilege.label.toLowerCase(),
  ];
  return candidates.some((key) => Boolean(permissions?.[key]));
};

const toCreatePermissions = (selectedPrivilegeValues: string[]) =>
  PRIVILEGES.reduce<Record<string, boolean>>((acc, privilege) => {
    acc[privilege.apiKey] = selectedPrivilegeValues.includes(privilege.value);
    return acc;
  }, {});

const toUpdatePermissions = (selectedPrivilegeValues: string[]) =>
  PRIVILEGES.reduce<Record<string, boolean>>((acc, privilege) => {
    acc[privilege.apiKey] = selectedPrivilegeValues.includes(privilege.value);
    return acc;
  }, {});

const mapSelectedPrivileges = (permissions?: Record<string, boolean>) =>
  PRIVILEGES.filter((p) => hasPermissionFlag(p, permissions)).map((p) => p.value);
const PASSWORD_COMPLEXITY_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/;

const mapSubAdminRows = (responseData: any): SubAdminRow[] => {
  const rawList: any[] = Array.isArray(responseData?.data?.accounts)
    ? responseData.data.accounts
    : Array.isArray(responseData?.data?.members)
      ? responseData.data.members
      : Array.isArray(responseData?.data?.list)
        ? responseData.data.list
        : Array.isArray(responseData?.data)
          ? responseData.data
          : [];

  return rawList.map((item) => ({
    id:
      item?.sub_admin_account_id ??
      item?.subAdminAccountId ??
      item?.id ??
      item?._id ??
      "",
    username: item?.username ?? item?.user_name ?? "",
    fullName: item?.name ?? item?.full_name ?? item?.display_name ?? "",
    isActive: Boolean(item?.is_active ?? item?.status ?? true),
    permissions: item?.permissions ?? {},
  }));
};

const CreateAccountPage = () => {
  const [selectedPrivileges, setSelectedPrivileges] = useState<string[]>([]);
  const [tableSearch, _setTableSearch] = useState("");
  const [rows, setRows] = useState<SubAdminRow[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [formErrors, setFormErrors] = useState<Partial<Record<"uname" | "fullname" | "password" | "cpass", string>>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [passwordModal, setPasswordModal] = useState<{ open: boolean; id: string; username: string }>({
    open: false,
    id: "",
    username: "",
  });
  const [passwordForm, setPasswordForm] = useState({
    new_password: "",
    confirm_password: "",
    transaction_password: "",
  });
  const [statusModal, setStatusModal] = useState<{
    open: boolean;
    id: string;
    username: string;
    isActive: boolean;
    transactionCode: string;
  }>({
    open: false,
    id: "",
    username: "",
    isActive: false,
    transactionCode: "",
  });

  const [formValues, setFormValues] = useState({
    uname: "",
    fullname: "",
    password: "",
    cpass: "",
    mpass: "",
  });

  const allSelected = useMemo(
    () => PRIVILEGES.length > 0 && selectedPrivileges.length === PRIVILEGES.length,
    [selectedPrivileges]
  );

  const fetchAccounts = async (search = "") => {
    setLoadingList(true);
    try {
      const { data } = await instance.get(endpoints.subAdminAccounts.list, {
        params: search ? { search } : {},
      });
      setRows(mapSubAdminRows(data));
    } catch (error: any) {
      const message = error?.response?.data?.status_message || "Failed to load accounts";
      showErrorToast(message);
      setRows([]);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    const id = setTimeout(() => {
      fetchAccounts(tableSearch.trim());
    }, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableSearch]);

  const handleToggleAll = (checked: boolean) => {
    setSelectedPrivileges(checked ? PRIVILEGES.map((p) => p.value) : []);
  };

  const handleTogglePrivilege = (value: string, checked: boolean) => {
    setSelectedPrivileges((prev) => {
      if (checked) return Array.from(new Set([...prev, value]));
      return prev.filter((v) => v !== value);
    });
  };

  const validateForm = () => {
    const nextErrors: Partial<Record<"uname" | "fullname" | "password" | "cpass", string>> = {};

    if (!formValues.uname.trim()) {
      nextErrors.uname = "The Client ID field is required";
    } else if (formValues.uname.trim().length < 4) {
      nextErrors.uname = "The Client ID field must be at least 4 characters";
    }

    if (!formValues.fullname.trim()) {
      nextErrors.fullname = "The Full Name field is required";
    } else if (formValues.fullname.trim().length < 4) {
      nextErrors.fullname = "The Full Name field must be at least 4 characters";
    }

    if (!isUpdateMode) {
      if (!formValues.password) {
        nextErrors.password = "The Password field is required";
      } else if (formValues.password.length < 8) {
        nextErrors.password = "The Password field must be at least 8 characters";
      } else if (!PASSWORD_COMPLEXITY_REGEX.test(formValues.password)) {
        nextErrors.password =
          "The password must contain at least: 1 uppercase letter, 1 lowercase letter, 1 number";
      }

      if (!formValues.cpass) {
        nextErrors.cpass = "The Confirm Password field is required";
      } else if (formValues.cpass !== formValues.password) {
        nextErrors.cpass = "The Confirm Password confirmation does not match";
      }
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateField = (field: CreateAccountField, values = formValues) => {
    if (field === "uname") {
      if (!values.uname.trim()) return "The Client ID field is required";
      if (values.uname.trim().length < 4) return "The Client ID field must be at least 4 characters";
      return undefined;
    }

    if (field === "fullname") {
      if (!values.fullname.trim()) return "The Full Name field is required";
      if (values.fullname.trim().length < 4) return "The Full Name field must be at least 4 characters";
      return undefined;
    }

    if (isUpdateMode) return undefined;

    if (field === "password") {
      if (!values.password) return "The Password field is required";
      if (values.password.length < 8) return "The Password field must be at least 8 characters";
      if (!PASSWORD_COMPLEXITY_REGEX.test(values.password)) {
        return "The password must contain at least: 1 uppercase letter, 1 lowercase letter, 1 number";
      }
      return undefined;
    }

    if (field === "cpass") {
      if (!values.cpass) return "The Confirm Password field is required";
      if (values.cpass !== values.password) return "The Confirm Password confirmation does not match";
    }

    return undefined;
  };

  const handleFieldBlur = (field: CreateAccountField) => {
    const error = validateField(field);
    setFormErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };

  const handleFieldChange = (field: CreateAccountField, value: string) => {
    const nextValues = { ...formValues, [field]: value };
    setFormValues(nextValues);

    const nextErrors: Partial<Record<CreateAccountField, string>> = {
      ...formErrors,
      [field]: validateField(field, nextValues),
    };

    // Keep confirm-password validation in sync when password changes.
    if (field === "password" && !isUpdateMode) {
      nextErrors.cpass = validateField("cpass", nextValues);
    }

    setFormErrors(nextErrors);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      if (isUpdateMode && editingId) {
        const payload = {
          name: formValues.fullname.trim(),
          permissions: toUpdatePermissions(selectedPrivileges),
        };
        await instance.put(endpoints.subAdminAccounts.update(editingId), payload);
        showSuccessToast("Account updated successfully");
      } else {
        const payload = {
          username: formValues.uname.trim(),
          name: formValues.fullname.trim(),
          password: formValues.password,
          permissions: toCreatePermissions(selectedPrivileges),
        };
        await instance.post(endpoints.subAdminAccounts.create, payload);
        showSuccessToast("Account created successfully");
      }

      handleReset();
      await fetchAccounts(tableSearch.trim());
    } catch (error: any) {
      const message = error?.response?.data?.status_message || "Something went wrong";
      showErrorToast(message);
    } finally {
      setSaving(false);
    }
  };

  const openStatusModal = (row: SubAdminRow) => {
    setStatusModal({
      open: true,
      id: row.id,
      username: row.username,
      isActive: row.isActive,
      transactionCode: "",
    });
  };

  const closeStatusModal = () => {
    setStatusModal({
      open: false,
      id: "",
      username: "",
      isActive: false,
      transactionCode: "",
    });
  };

  const updateStatus = async (nextIsActive: boolean) => {
    if (!statusModal.id) {
      showErrorToast("Sub admin account id is missing");
      return;
    }
    if (!statusModal.transactionCode.trim()) {
      showErrorToast("Transaction Code is required");
      return;
    }

    try {
      await instance.patch(endpoints.subAdminAccounts.status(statusModal.id), {
        is_active: nextIsActive,
        transaction_password: statusModal.transactionCode.trim(),
      });
      showSuccessToast("Status updated successfully");
      closeStatusModal();
      await fetchAccounts(tableSearch.trim());
    } catch (error: any) {
      const message = error?.response?.data?.status_message || "Failed to update status";
      showErrorToast(message);
    }
  };

  const handleStatusToggle = async (e: FormEvent) => {
    e.preventDefault();
    await updateStatus(statusModal.isActive);
  };

  const handleEdit = (row: SubAdminRow) => {
    setIsUpdateMode(true);
    setEditingId(row.id);
    setFormValues((prev) => ({
      ...prev,
      uname: row.username,
      fullname: row.fullName,
      password: "",
      cpass: "",
      mpass: "",
    }));
    setSelectedPrivileges(mapSelectedPrivileges(row.permissions));
    setFormErrors({});
  };

  const openPasswordModal = (row: SubAdminRow) => {
    setPasswordModal({ open: true, id: row.id, username: row.username });
    setPasswordForm({
      new_password: "",
      confirm_password: "",
      transaction_password: "",
    });
  };

  const closePasswordModal = () => {
    setPasswordModal({ open: false, id: "", username: "" });
  };

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!passwordForm.new_password || !passwordForm.confirm_password) {
      showErrorToast("New password and confirm password are required");
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      showErrorToast("New password and confirm password must be same");
      return;
    }
    if (!passwordForm.transaction_password) {
      showErrorToast("Transaction code is required");
      return;
    }

    try {
      await instance.post(endpoints.subAdminAccounts.changePassword(passwordModal.id), passwordForm);
      showSuccessToast("Password changed successfully");
      closePasswordModal();
    } catch (error: any) {
      const message = error?.response?.data?.status_message || "Failed to change password";
      showErrorToast(message);
    }
  };

  const handleReset = () => {
    setFormValues({
      uname: "",
      fullname: "",
      password: "",
      cpass: "",
      mpass: "",
    });
    setEditingId(null);
    setIsUpdateMode(false);
    setSelectedPrivileges([]);
    setFormErrors({});
  };

  return (
    <div>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-0 font-size-18">Multi Login Account</h4>
            <div className="page-title-right" />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body create-account-container">
              <form method="post" onSubmit={handleSubmit}>
                <div className="create-account-form">
                  <div>
                    <h5 className="mb-0">Personal Information</h5>
                    <div className="row">
                      <div className="col-md-3 form-group">
                        <label htmlFor="uname">Client ID</label>
                        <input
                          id="uname"
                          type="text"
                          name="uname"
                          className={`form-control ${formErrors.uname ? "is-invalid" : ""}`}
                          value={formValues.uname}
                          readOnly={isUpdateMode}
                          aria-required="true"
                          aria-invalid={Boolean(formErrors.uname)}
                          onChange={(e) => handleFieldChange("uname", e.target.value)}
                          onBlur={() => handleFieldBlur("uname")}
                        />
                        {formErrors.uname ? <small className="error">{formErrors.uname}</small> : null}
                      </div>
                      <div className="col-md-3 form-group">
                        <label htmlFor="fullname">Full Name</label>
                        <input
                          id="fullname"
                          type="text"
                          name="fullname"
                          className={`form-control ${formErrors.fullname ? "is-invalid" : ""}`}
                          value={formValues.fullname}
                          aria-required="true"
                          aria-invalid={Boolean(formErrors.fullname)}
                          onChange={(e) => handleFieldChange("fullname", e.target.value)}
                          onBlur={() => handleFieldBlur("fullname")}
                        />
                        {formErrors.fullname ? <small className="error">{formErrors.fullname}</small> : null}
                      </div>
                      {!isUpdateMode && (
                        <div className="col-md-3 form-group">
                          <label htmlFor="password">Password</label>
                          <input
                            id="password"
                            type="password"
                            name="password"
                            className={`form-control ${formErrors.password ? "is-invalid" : ""}`}
                            value={formValues.password}
                            aria-required="true"
                            aria-invalid={Boolean(formErrors.password)}
                            onChange={(e) => handleFieldChange("password", e.target.value)}
                            onBlur={() => handleFieldBlur("password")}
                          />
                          {formErrors.password ? <small className="error">{formErrors.password}</small> : null}
                        </div>
                      )}
                      {!isUpdateMode && (
                        <div className="col-md-3 form-group">
                          <label htmlFor="cpass">Confirm Password</label>
                          <input
                            id="cpass"
                            type="password"
                            name="cpass"
                            className={`form-control ${formErrors.cpass ? "is-invalid" : ""}`}
                            value={formValues.cpass}
                            aria-required="true"
                            aria-invalid={Boolean(formErrors.cpass)}
                            onChange={(e) => handleFieldChange("cpass", e.target.value)}
                            onBlur={() => handleFieldBlur("cpass")}
                          />
                          {formErrors.cpass ? <small className="error">{formErrors.cpass}</small> : null}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-2 previlages">
                    <h5 className="mb-0">Privileges</h5>
                    <div className="previlage-box">
                      <div className="previlage-item">
                        <div>
                          <div className="custom-control custom-checkbox">
                            <input
                              type="checkbox"
                              name="plist_all"
                              className="custom-control-input"
                              checked={allSelected}
                              id="privilege-all"
                              onChange={(e) => handleToggleAll(e.target.checked)}
                            />
                            <label className="custom-control-label" htmlFor="privilege-all">
                              All
                            </label>
                          </div>
                        </div>
                      </div>
                      <br />
                      {PRIVILEGES.map((privilege) => (
                        <div className="previlage-item" key={privilege.id}>
                          <div>
                            <div className="custom-control custom-checkbox">
                              <input
                                type="checkbox"
                                name="plist"
                                className="custom-control-input"
                                value={privilege.value}
                                id={privilege.id}
                                checked={selectedPrivileges.includes(privilege.value)}
                                onChange={(e) => handleTogglePrivilege(privilege.value, e.target.checked)}
                              />
                              <label className="custom-control-label" htmlFor={privilege.id}>
                                {privilege.label}
                              </label>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="previlage-master mt-2">
                      <div className="form-group mb-0">
                        <input
                          type="password"
                          name="mpass"
                          placeholder="Transaction Code"
                          className="form-control mpass-text"
                          value={formValues.mpass}
                          onChange={(e) => setFormValues((prev) => ({ ...prev, mpass: e.target.value }))}
                        />
                        <button type="submit" className="btn btn-success ml-2">
                          {saving ? "Saving..." : isUpdateMode ? "Update" : "Submit"}
                        </button>
                        <button type="button" id="reset" className="btn btn-light ml-2" onClick={handleReset}>
                          Reset
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>

              <div className="outer mt-4">
                <div className="inner">
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <th className="fixed-col-1">Action</th>
                        <th className="fixed-col-2">Username</th>
                        <th className="fixed-col-3">Full Name</th>
                        {PRIVILEGES.map((privilege) => (
                          <th key={`head-${privilege.id}`}>{privilege.label}</th>
                        ))}
                      </tr>
                      {loadingList ? (
                        <tr>
                          <td colSpan={PRIVILEGES.length + 3} className="text-center">
                            Loading...
                          </td>
                        </tr>
                      ) : rows.length === 0 ? (
                        <tr>
                          <td colSpan={PRIVILEGES.length + 3} className="text-center">
                            No data available
                          </td>
                        </tr>
                      ) : (
                        rows.map((row) => (
                          <tr key={row.id}>
                            <td className="fixed-col-1">
                              <button
                                type="button"
                                className="text-white btn btn-primary btn-sm mr-1"
                                onClick={() => handleEdit(row)}
                                title="Update"
                              >
                                U
                              </button>
                              <button
                                type="button"
                                className="text-white btn btn-info btn-sm mr-1"
                                onClick={() => openStatusModal(row)}
                                title="Change User Status"
                              >
                                S
                              </button>
                              <button
                                type="button"
                                className="text-white btn btn-success btn-sm"
                                onClick={() => openPasswordModal(row)}
                                title="Change Password"
                              >
                                P
                              </button>
                            </td>
                            <td className="fixed-col-2">
                              {row.username}
                              <i className={`${row.isActive ? "text-success" : "text-light"} fas fa-check pl-2`} />
                            </td>
                            <td className="fixed-col-3">{row.fullName}</td>
                            {PRIVILEGES.map((privilege) => (
                              <td key={`cell-${row.id}-${privilege.id}`} className="text-center">
                                <i
                                  className={`fas ${
                                    hasPermissionFlag(privilege, row.permissions)
                                      ? "fa-check-circle text-success"
                                      : "fa-times-circle text-muted"
                                  }`}
                                />
                              </td>
                            ))}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {passwordModal.open && (
        <div style={{ position: "absolute", zIndex: 1040 }}>
          <div className="modal fade show" style={{ display: "block" }} role="dialog" aria-modal="true">
            <div className="modal-dialog modal-md">
              <span />
              <div className="modal-content" tabIndex={-1}>
                <header className="modal-header">
                  <h5 className="modal-title">Change Password</h5>
                  <button type="button" className="close" aria-label="Close" onClick={closePasswordModal}>
                    ×
                  </button>
                </header>
                <form method="post" onSubmit={handlePasswordSubmit}>
                  <div className="modal-body">
                    <div className="form-group row">
                      <label className="col-form-label col-4" htmlFor="cppassword">
                        Password
                      </label>
                      <div className="col-8 form-group-feedback form-group-feedback-right">
                        <input
                          id="cppassword"
                          placeholder="Password"
                          type="password"
                          name="cppassword"
                          className="form-control"
                          aria-required="true"
                          value={passwordForm.new_password}
                          onChange={(e) =>
                            setPasswordForm((prev) => ({ ...prev, new_password: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                    <div className="form-group row">
                      <label className="col-form-label col-4" htmlFor="cpcpassword">
                        Confirm Password
                      </label>
                      <div className="col-8 form-group-feedback form-group-feedback-right">
                        <input
                          id="cpcpassword"
                          placeholder="Confirm Password"
                          type="password"
                          name="cpcpassword"
                          className="form-control"
                          aria-required="true"
                          value={passwordForm.confirm_password}
                          onChange={(e) =>
                            setPasswordForm((prev) => ({ ...prev, confirm_password: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                    <div className="form-group row">
                      <label className="col-form-label col-4" htmlFor="cpmpassword">
                        Transaction Code
                      </label>
                      <div className="col-8 form-group-feedback form-group-feedback-right">
                        <input
                          id="cpmpassword"
                          placeholder="Transaction Code"
                          type="password"
                          name="cpmpassword"
                          className="form-control"
                          aria-required="true"
                          value={passwordForm.transaction_password}
                          onChange={(e) =>
                            setPasswordForm((prev) => ({ ...prev, transaction_password: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                    <div className="form-group row">
                      <div className="col-12 text-right">
                        <button type="submit" className="btn btn-primary">
                          submit
                          <i className="fas fa-sign-in-alt ml-1" />
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              <span />
            </div>
          </div>
          <div className="modal-backdrop" />
        </div>
      )}

      {statusModal.open && (
        <div style={{ position: "absolute", zIndex: 1040 }}>
          <div role="dialog" className="modal fade show" aria-modal="true" style={{ display: "block" }}>
            <div className="modal-dialog modal-md">
              <span />
              <div className="modal-content" tabIndex={-1}>
                <header className="modal-header">
                  <h5 className="modal-title">Change User Status</h5>
                  <button type="button" aria-label="Close" className="close" onClick={closeStatusModal}>
                    ×
                  </button>
                </header>
                <div className="modal-body">
                  <form method="post" onSubmit={handleStatusToggle}>
                    <div className="form-group row">
                      <label className="col-form-label col-4" htmlFor="user-lock-switch">
                        User lock
                      </label>
                      <label
                        className={`vue-js-switch${statusModal.isActive ? " toggled" : ""}`}
                        htmlFor="user-lock-switch"
                      >
                        <input
                          id="user-lock-switch"
                          type="checkbox"
                          className="v-switch-input"
                          checked={statusModal.isActive}
                          onChange={(event) =>
                            setStatusModal((prev) => ({
                              ...prev,
                              isActive: event.target.checked,
                            }))
                          }
                        />
                        <div
                          className="v-switch-core"
                          style={{
                            width: "50px",
                            height: "22px",
                            backgroundColor: statusModal.isActive ? "rgb(0, 136, 204)" : "rgb(189, 189, 189)",
                            borderRadius: "11px",
                          }}
                        >
                          <div
                            className="v-switch-button"
                            style={{
                              width: "16px",
                              height: "16px",
                              transition: "transform 300ms",
                              transform: statusModal.isActive
                                ? "translate3d(31px, 3px, 0px)"
                                : "translate3d(3px, 3px, 0px)",
                            }}
                          />
                        </div>
                        <span
                          className={`v-switch-label ${statusModal.isActive ? "v-left" : "v-right"}`}
                          style={{ lineHeight: "22px" }}
                        >
                          {statusModal.isActive ? "on" : "off"}
                        </span>
                      </label>
                    </div>
                    <div className="form-group row">
                      <label className="col-form-label col-4" htmlFor="userLockMpassword">
                        Transaction Code
                      </label>
                      <div className="col-8 form-group-feedback-right pl-0">
                        <input
                          id="userLockMpassword"
                          placeholder="Transaction Code"
                          type="password"
                          name="UserLockMpassword"
                          className="form-control"
                          aria-required="true"
                          value={statusModal.transactionCode}
                          onChange={(event) =>
                            setStatusModal((prev) => ({
                              ...prev,
                              transactionCode: event.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                    <div className="form-group row">
                      <div className="col-12 text-right">
                        <button type="submit" className="btn btn-primary">
                          submit
                          <i className="fas fa-sign-in-alt ml-1" />
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
              <span />
            </div>
          </div>
          <div className="modal-backdrop" />
        </div>
      )}
    </div>
  );
};

export default CreateAccountPage;
